/**
 * Auth store — current user info (decoded JWT claims).
 * Token storage lives in oidc-client-ts; this store is just the React-visible projection.
 */
import type { LoginRole, Role } from '@/lib/auth/access';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  initials: string;
  roles: string[];
  tenantId: string;
  isAdmin: boolean;
  // Canonical 4-layer role driving access control (Sprint 26 86e232yn7).
  // `roles` above stays free-form display labels; `role` is the gate.
  role: LoginRole;
}

interface AuthStore {
  user: AuthUser | null;
  setUser: (u: AuthUser | null) => void;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    // Persist the signed-in user so the session (and its role) survives a
    // page refresh. A first-time visitor has no persisted user → `public`.
    persist(
      (set) => ({
        user: null,
        setUser: (user) => set({ user }),
      }),
      { name: 'flysafe-v2-auth', partialize: (s) => ({ user: s.user }) },
    ),
    { name: 'auth' },
  ),
);

/**
 * Effective role of the viewer: the signed-in user's role, or `public` when
 * unauthenticated. Use this (not `user`) for every access decision.
 */
export function useCurrentRole(): Role {
  return useAuthStore((s) => s.user?.role ?? 'public');
}

/* Mock users per role for the dev login flow (no real Keycloak yet). */
const MOCK_USERS: Record<LoginRole, AuthUser> = {
  admin: {
    id: 'mock-admin',
    email: 'admin@airmarket.io',
    fullName: 'Demo Admin',
    initials: 'DA',
    roles: ['Administrator'],
    tenantId: 'mock',
    isAdmin: true,
    role: 'admin',
  },
  pilot: {
    id: 'mock-pilot',
    email: 'pilot@airmarket.io',
    fullName: 'Demo Pilot',
    initials: 'DP',
    roles: ['Pilot'],
    tenantId: 'mock',
    isAdmin: false,
    role: 'pilot',
  },
  user: {
    id: 'mock-user',
    email: 'user@airmarket.io',
    fullName: 'Demo User',
    initials: 'DU',
    roles: ['Viewer'],
    tenantId: 'mock',
    isAdmin: false,
    role: 'user',
  },
};

export function mockUserForRole(role: LoginRole): AuthUser {
  return MOCK_USERS[role];
}

/** Back-compat default used by existing call sites (admin identity). */
export const MOCK_USER: AuthUser = MOCK_USERS.admin;
